import type { IssueItem } from "@/hooks/queries/use-issues";
import { useFeature, useUpdateFeature } from "@/hooks/queries/use-features";
import { useTask, useUpdateTask } from "@/hooks/queries/use-tasks";
import { useStatuses } from "@/hooks/queries/use-statuses";
import { useUsers } from "@/hooks/queries/use-users";
import { TaskList } from "@/components/tasks/task-list";
import { MarkdownEditor } from "@/components/common/markdown-editor";
import { MarkdownRenderer } from "@/components/common/markdown-renderer";
import { ActivityPanel } from "@/components/activity/activity-panel";
import DecisionsPanel from "@/components/decisions/decisions-panel";
import CodeContextPanel from "@/components/code-context/code-context-panel";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getInitials } from "@/lib/get-initials";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { X, Circle, CheckCircle2, User, ExternalLink, Terminal, Copy, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { useState, useCallback } from "react";
import { HELP_IDS } from "@/lib/help-ids";
import {
  buildOrchestratorFeatureCommand,
  getAgentPromptExplanation,
} from "@/lib/chat/cli-guidance";
import { useResolvedHost } from "@/hooks/use-resolved-host";

const StatusIcon = ({ category, className }: { category: string | undefined; className?: string }) => {
  if (category === "completed") return <CheckCircle2 className={cn("h-3.5 w-3.5 text-green-500", className)} />;
  if (category === "canceled") return <Circle className={cn("h-3.5 w-3.5 text-red-500", className)} />;
  if (category === "started") return <Circle className={cn("h-3.5 w-3.5 text-yellow-500 fill-yellow-500/30", className)} />;
  return <Circle className={cn("h-3.5 w-3.5 text-muted-foreground", className)} />;
};

interface SplitDetailPanelProps {
  item: IssueItem;
  onClose: () => void;
}

type PanelTab = 'description' | 'activity' | 'decisions' | 'code' | 'edit';
const panelTabs: { id: PanelTab; label: string }[] = [
  { id: 'description', label: 'Description' },
  { id: 'activity', label: 'Activity' },
  { id: 'decisions', label: 'Decisions' },
  { id: 'code', label: 'Code' },
  { id: 'edit', label: 'Edit' },
];

function FeaturePanel({ featureId, onClose }: { featureId: string; onClose: () => void }) {
  const { data: feature, isLoading } = useFeature(featureId);
  const updateFeature = useUpdateFeature();
  const { data: statuses } = useStatuses(feature?.epic?.teamId);
  const { data: users } = useUsers();
  const host = useResolvedHost();
  const [panelTab, setPanelTab] = useState<PanelTab>('description');
  const [copied, setCopied] = useState(false);

  const handleCopyCommand = useCallback(async (command: string) => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = command;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, []);

  const currentStatus = statuses?.find((s) => s.id === feature?.statusId) ?? feature?.status;
  const currentAssignee = users?.find((u) => u.id === feature?.assigneeId) ?? feature?.assignee;

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-6 w-48 animate-pulse rounded bg-muted" />
        <div className="h-4 w-full animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (!feature) return <div className="p-6 text-muted-foreground text-sm">Feature not found</div>;

  const handleDescriptionChange = async (description: string) => {
    if (description !== (feature.description ?? "")) {
      await updateFeature.mutateAsync({ id: feature.id, description: description || undefined });
    }
  };

  const handleStatusChange = async (statusId: string) => {
    await updateFeature.mutateAsync({ id: feature.id, statusId });
  };

  const handleAssigneeChange = async (assigneeId: string | undefined) => {
    const data: Parameters<typeof updateFeature.mutateAsync>[0] = { id: feature.id };
    if (assigneeId) data.assigneeId = assigneeId;
    await updateFeature.mutateAsync(data);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Panel header */}
      <div className="px-5 py-3 border-b border-white/[0.06]">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <span className="text-xs text-muted-foreground font-mono">{feature.identifier}</span>
            <h2 className="text-sm font-semibold mt-0.5">{feature.title}</h2>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Link to={`/features/${feature.id}`}>
              <Button variant="ghost" size="icon" className="h-11 w-11 md:h-7 md:w-7" title="Open full page">
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            </Link>
            <Button variant="ghost" size="icon" className="h-11 w-11 md:h-7 md:w-7" onClick={onClose}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Tab row */}
      <div className="flex items-center gap-1 px-5 py-2 border-b border-white/[0.06]">
        {panelTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => { setPanelTab(tab.id); }}
            className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
              panelTab === tab.id
                ? 'bg-white/[0.08] text-foreground font-medium'
                : 'text-muted-foreground hover:text-foreground hover:bg-white/[0.04]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Properties */}
      <div className="flex items-center gap-3 px-5 py-2 border-b border-white/[0.06] text-sm">
        <span className="text-xs px-2 py-0.5 rounded bg-white/[0.06] text-muted-foreground">Feature</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-white/[0.04] transition-colors">
              <StatusIcon category={currentStatus?.category} />
              <span className="text-xs">{currentStatus?.name ?? "Status"}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {statuses?.map((status) => (
              <DropdownMenuItem key={status.id} onClick={() => void handleStatusChange(status.id)} className="flex items-center gap-2">
                <StatusIcon category={status.category} />
                <span className="text-xs">{status.name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-white/[0.04] transition-colors">
              {currentAssignee ? (
                <>
                  <Avatar className="h-4 w-4">
                    {currentAssignee.avatarUrl && <AvatarImage src={currentAssignee.avatarUrl} alt={currentAssignee.name} />}
                    <AvatarFallback className="text-[10px]">{getInitials(currentAssignee.name)}</AvatarFallback>
                  </Avatar>
                  <span className="text-xs">{currentAssignee.name}</span>
                </>
              ) : (
                <>
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Assign</span>
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            <DropdownMenuItem onClick={() => void handleAssigneeChange(undefined)} className="flex items-center gap-2 text-muted-foreground">
              <User className="h-3.5 w-3.5" />
              <span className="text-xs">Unassigned</span>
            </DropdownMenuItem>
            {users?.map((user) => (
              <DropdownMenuItem key={user.id} onClick={() => void handleAssigneeChange(user.id)} className="flex items-center gap-2">
                <Avatar className="h-4 w-4">
                  {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.name} />}
                  <AvatarFallback className="text-[10px]">{getInitials(user.name)}</AvatarFallback>
                </Avatar>
                <span className="text-xs">{user.name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {feature.epic && (
          <div className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
            <div className="h-2.5 w-2.5 rounded-sm bg-primary/30" />
            {feature.epic.name}
          </div>
        )}
      </div>

      {/* Execute this Feature command */}
      {feature.epic && !feature.epic.isArchived && (
        <div className="border-b border-white/[0.06] px-5 py-2.5">
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
            <Terminal className="h-3.5 w-3.5" />
            <span>Execute this Feature</span>
          </div>
          <div className="flex items-center gap-1.5">
            <code className="flex-1 rounded-md bg-white/[0.03] border border-white/[0.06] px-2.5 py-1.5 text-xs font-mono select-all" data-help-id={HELP_IDS.feature.execute.command}>
              {buildOrchestratorFeatureCommand(feature.identifier, host)}
            </code>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={() => { void handleCopyCommand(buildOrchestratorFeatureCommand(feature.identifier, host)); }}
              data-help-id={HELP_IDS.feature.execute.copyButton}
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-green-600" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {getAgentPromptExplanation(host)}
          </p>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto p-5 pb-24 space-y-4">
        {panelTab === 'description' && (
          <>
            {feature.description ? (
              <MarkdownRenderer content={feature.description} className="text-sm prose-sm" />
            ) : (
              <p className="text-sm text-muted-foreground italic">No description</p>
            )}
            <div className="border-t border-white/[0.06] pt-4">
              <TaskList featureId={feature.id} teamId={feature.epic?.teamId} />
            </div>
          </>
        )}
        {panelTab === 'activity' && (
          <ActivityPanel featureId={feature.id} />
        )}
        {panelTab === 'decisions' && (
          <DecisionsPanel featureId={feature.id} />
        )}
        {panelTab === 'code' && (
          <CodeContextPanel featureId={feature.id} />
        )}
        {panelTab === 'edit' && (
          <MarkdownEditor
            value={feature.description ?? ""}
            onChange={(value) => void handleDescriptionChange(value)}
            placeholder="Add a description..."
          />
        )}
      </div>
    </div>
  );
}

function TaskPanel({ taskId, onClose }: { taskId: string; onClose: () => void }) {
  const { data: task, isLoading } = useTask(taskId);
  const updateTask = useUpdateTask();
  const teamId = task?.feature?.epic?.teamId;
  const { data: statuses } = useStatuses(teamId);
  const { data: users } = useUsers();
  const [panelTab, setPanelTab] = useState<PanelTab>('description');

  const currentStatus = statuses?.find((s) => s.id === task?.statusId) ?? task?.status;
  const currentAssignee = users?.find((u) => u.id === task?.assigneeId) ?? task?.assignee;

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-6 w-48 animate-pulse rounded bg-muted" />
        <div className="h-4 w-full animate-pulse rounded bg-muted" />
        <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (!task) return <div className="p-6 text-muted-foreground text-sm">Task not found</div>;

  const handleDescriptionChange = async (description: string) => {
    if (description !== (task.description ?? "")) {
      await updateTask.mutateAsync({ id: task.id, description: description || undefined });
    }
  };

  const handleStatusChange = async (statusId: string) => {
    await updateTask.mutateAsync({ id: task.id, statusId });
  };

  const handleAssigneeChange = async (assigneeId: string | undefined) => {
    const data: Parameters<typeof updateTask.mutateAsync>[0] = { id: task.id };
    if (assigneeId) data.assigneeId = assigneeId;
    await updateTask.mutateAsync(data);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Panel header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
        <div className="min-w-0">
          <span className="text-xs text-muted-foreground font-mono">{task.identifier}</span>
          <h2 className="text-sm font-semibold mt-0.5">{task.title}</h2>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {task.featureId && (
            <Link to={`/features/${task.featureId}`}>
              <Button variant="ghost" size="icon" className="h-11 w-11 md:h-7 md:w-7" title="Open feature">
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            </Link>
          )}
          <Button variant="ghost" size="icon" className="h-11 w-11 md:h-7 md:w-7" onClick={onClose}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Tab row */}
      <div className="flex items-center gap-1 px-5 py-2 border-b border-white/[0.06]">
        {panelTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => { setPanelTab(tab.id); }}
            className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
              panelTab === tab.id
                ? 'bg-white/[0.08] text-foreground font-medium'
                : 'text-muted-foreground hover:text-foreground hover:bg-white/[0.04]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Properties */}
      <div className="flex items-center gap-3 px-5 py-2 border-b border-white/[0.06] text-sm">
        <span className="text-xs px-2 py-0.5 rounded bg-white/[0.06] text-muted-foreground">Task</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-white/[0.04] transition-colors">
              <StatusIcon category={currentStatus?.category} />
              <span className="text-xs">{currentStatus?.name ?? "Status"}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {statuses?.map((status) => (
              <DropdownMenuItem key={status.id} onClick={() => void handleStatusChange(status.id)} className="flex items-center gap-2">
                <StatusIcon category={status.category} />
                <span className="text-xs">{status.name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-white/[0.04] transition-colors">
              {currentAssignee ? (
                <>
                  <Avatar className="h-4 w-4">
                    {currentAssignee.avatarUrl && <AvatarImage src={currentAssignee.avatarUrl} alt={currentAssignee.name} />}
                    <AvatarFallback className="text-[10px]">{getInitials(currentAssignee.name)}</AvatarFallback>
                  </Avatar>
                  <span className="text-xs">{currentAssignee.name}</span>
                </>
              ) : (
                <>
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Assign</span>
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            <DropdownMenuItem onClick={() => void handleAssigneeChange(undefined)} className="flex items-center gap-2 text-muted-foreground">
              <User className="h-3.5 w-3.5" />
              <span className="text-xs">Unassigned</span>
            </DropdownMenuItem>
            {users?.map((user) => (
              <DropdownMenuItem key={user.id} onClick={() => void handleAssigneeChange(user.id)} className="flex items-center gap-2">
                <Avatar className="h-4 w-4">
                  {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.name} />}
                  <AvatarFallback className="text-[10px]">{getInitials(user.name)}</AvatarFallback>
                </Avatar>
                <span className="text-xs">{user.name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {task.feature && (
          <div className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
            <div className="h-2.5 w-2.5 rounded-sm bg-blue-500/30" />
            {task.feature.title}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-5 pb-24">
        {panelTab === 'description' && (
          task.description ? (
            <MarkdownRenderer content={task.description} className="text-sm prose-sm" />
          ) : (
            <p className="text-sm text-muted-foreground italic">No description</p>
          )
        )}
        {panelTab === 'activity' && (
          <ActivityPanel taskId={task.id} />
        )}
        {panelTab === 'decisions' && (
          <DecisionsPanel taskId={task.id} />
        )}
        {panelTab === 'code' && (
          <CodeContextPanel taskId={task.id} />
        )}
        {panelTab === 'edit' && (
          <MarkdownEditor
            value={task.description ?? ""}
            onChange={(value) => void handleDescriptionChange(value)}
            placeholder="Add a description..."
          />
        )}
      </div>
    </div>
  );
}

export function SplitDetailPanel({ item, onClose }: SplitDetailPanelProps) {
  if (item.type === "feature") {
    return <FeaturePanel featureId={item.data.id} onClose={onClose} />;
  }
  return <TaskPanel taskId={item.data.id} onClose={onClose} />;
}
